import { useContext, useEffect, useState } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { IconCheck, IconLoader2, IconShare2, IconX } from '@tabler/icons-react'
import { domToPng } from 'modern-screenshot'
import { useTheme } from 'next-themes'
import { toast } from 'sonner'

import { cleanupScreenshotContext, getScreenshotContext } from '@/lib/screenshot/screenshot-context'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { MESSAGE_ICON_SIZE } from '@/components/messages/message-action-icons'

interface ChatMessageSelectorProps {
  messageId?: string
  isSelected?: boolean
  onSelect?: (messageId: string) => void
}

export const ChatMessageSelector = ({ messageId }: ChatMessageSelectorProps) => {
  const {
    selectedMessages,
    isSelecting,
    setSelectedMessages,
    setIsSelecting,
    handleMessageSelect,
    selectedChat
  } = useContext(ChatbotUIChatContext)

  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const { theme } = useTheme()

  const generateShareImage = async (): Promise<Blob | void> => {
    try {
      setIsProcessing(true)

      // Helper function to check if an image exists and is loadable
      const checkImageExists = async (url: string): Promise<boolean> => {
        return new Promise((resolve) => {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.decoding = 'sync'
          img.onload = () => resolve(true)
          img.onerror = () => {
            const fallbackImg = new Image()
            fallbackImg.decoding = 'sync'
            fallbackImg.onload = () => resolve(true)
            fallbackImg.onerror = () => resolve(false)
            fallbackImg.src = url
          }
          img.src = url
        })
      }

      // Helper function to get working thumbnail URL with proxy
      const getWorkingThumbnail = async (videoId: string): Promise<string | null> => {
        const qualities = ['maxresdefault', 'hqdefault', 'sddefault', 'default']

        for (const quality of qualities) {
          try {
            const response = await fetch(
              `/api/youtube/thumbnail?videoId=${videoId}&quality=${quality}`
            )
            if (response.ok) {
              const blob = await response.blob()
              return URL.createObjectURL(blob)
            }
          } catch {
            continue
          }
        }
        return null
      }

      const selectedElements = selectedMessages
        .map((id: string) => {
          const el = document.getElementById(`message-${id}`)?.closest('.group')
          if (!el) return null
          return { el, position: el.getBoundingClientRect().top }
        })
        .filter(Boolean)
        .sort((a, b) => a!.position - b!.position)
        .map((item) => item!.el) as HTMLElement[]

      if (selectedElements.length === 0) {
        toast.error('No messages found to generate image')
        return
      }

      // Create container with unique ID to prevent conflicts
      const containerId = 'screenshot-container-' + Date.now()
      const container = document.createElement('div')
      container.id = containerId
      container.style.cssText = `
        padding: 48px;
        background: ${
          theme === 'dark'
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
        };
        width: 800px;
        
        box-shadow: ${
          theme === 'dark'
            ? '0 20px 25px -5px rgb(0 0 0 / 0.3), 0 8px 10px -6px rgb(0 0 0 / 0.3)'
            : '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
        };
        min-height: 200px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        position: relative;
        overflow: hidden;
      `

      // Update the branding container styling
      const brandingContainer = document.createElement('div')
      brandingContainer.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        margin-bottom: 24px;
        padding: 8px 0;
      `

      // Left side with logo and name
      const leftBranding = document.createElement('div')
      leftBranding.style.cssText = `
        display: flex;
        align-items: center;
        gap: 16px;
      `

      const brandingImage = document.createElement('img')
      brandingImage.src = '/logo.png'
      brandingImage.alt = 'Losi'
      brandingImage.decoding = 'sync'
      brandingImage.style.cssText = `
        width: 48px;
        height: 48px;
        border-radius: 12px;
      `

      const brandingText = document.createElement('span')
      brandingText.textContent = 'Losi Chat'
      brandingText.style.cssText = `
        font-size: 38px;
        font-weight: 700;
        color: ${theme === 'dark' ? '#ffffff' : '#1a1a1a'};
        white-space: nowrap;
        display: inline-block;
      `

      // Right side with chat name
      const rightInfo = document.createElement('div')
      rightInfo.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
      `

      const chatName = document.createElement('span')
      chatName.textContent = selectedChat?.name || 'Chat'
      chatName.style.cssText = `
        font-size: 18px;
        font-weight: 600;
        color: ${theme === 'dark' ? '#ffffff' : '#1a1a1a'};
      `

      const sharedFrom = document.createElement('span')
      sharedFrom.textContent = 'Shared from losi.one'
      sharedFrom.style.cssText = `
        font-size: 14px;
        color: ${theme === 'dark' ? '#a1a1aa' : '#71717a'};
      `

      // Assemble the components
      leftBranding.appendChild(brandingImage)
      leftBranding.appendChild(brandingText)
      rightInfo.appendChild(chatName)
      rightInfo.appendChild(sharedFrom)
      brandingContainer.appendChild(leftBranding)
      brandingContainer.appendChild(rightInfo)
      container.appendChild(brandingContainer)

      // Add inner container for messages with white background
      const messagesContainer = document.createElement('div')
      messagesContainer.style.cssText = `
        background: ${theme === 'dark' ? '#2d2d2d' : 'white'};
        border-radius: 32px;
        padding: 12px;
        box-shadow: ${
          theme === 'dark' ? '0 4px 6px -1px rgb(0 0 0 / 0.2)' : '0 4px 6px -1px rgb(0 0 0 / 0.1)'
        };
      `
      container.appendChild(messagesContainer)

      // Clone messages safely
      await Promise.all(
        selectedElements.map(async (el) => {
          const messageClone = el.cloneNode(true) as HTMLElement

          // Handle YouTube iframes
          const iframes = messageClone.querySelectorAll('iframe')
          await Promise.all(
            Array.from(iframes).map(async (iframe) => {
              try {
                const src = iframe.getAttribute('src') || ''
                const videoId = src.match(/\/embed\/([^?]+)/)?.[1]

                if (videoId) {
                  const thumbnailUrl = await getWorkingThumbnail(videoId)

                  if (thumbnailUrl) {
                    const wrapper = document.createElement('div')
                    wrapper.style.cssText = `
                      position: relative;
                      width: 100%;
                      min-height: 56.25vw; /* 16:9 aspect ratio */
                      background-color: #f0f0f0;
                      border-radius: 32px;
                      overflow: hidden;
                      margin: 8px 0;
                      display: inline-block;
                      vertical-align: top;
                      @media (min-width: 768px) {
                        width: calc(50% - 8px);
                        margin: 8px 4px;
                      }
                    `

                    // Create static placeholder instead of img
                    const placeholder = document.createElement('div')
                    placeholder.style.cssText = `
                      width: 100%;
                      height: 56.25vw; /* 16:9 aspect ratio */
                      background-image: url('${thumbnailUrl}');
                      background-size: cover;
                      background-position: center;
                      background-repeat: no-repeat;
                      border-radius: 32px;
                    `

                    const playButton = document.createElement('div')
                    playButton.style.cssText = `
                      position: absolute;
                      top: 50%;
                      left: 50%;
                      transform: translate(-50%, -50%);
                      width: 88px;
                      height: 68px;
                      background-color: rgba(255, 0, 0, 0.8);
                      border-radius: 16px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    `

                    const playSymbol = document.createElement('div')
                    playSymbol.style.cssText = `
                      width: 0;
                      height: 0;
                      border-style: solid;
                      border-width: 10px 0 10px 20px;
                      border-color: transparent transparent transparent white;
                      margin-left: 4px;
                    `

                    playButton.appendChild(playSymbol)
                    wrapper.appendChild(placeholder)
                    wrapper.appendChild(playButton)
                    iframe.parentNode?.replaceChild(wrapper, iframe)
                  } else {
                    // If no thumbnail available, create a simple placeholder
                    const fallback = document.createElement('div')
                    fallback.style.cssText = `
                      width: 100%;
                      height: 56.25vw; /* 16:9 aspect ratio */
                      background-color: #f0f0f0;
                      border-radius: 32px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      margin: 8px 0;
                      @media (min-width: 768px) {
                        width: calc(50% - 8px);
                        margin: 8px 4px;
                        display: inline-flex;
                      }
                    `
                    fallback.textContent = 'Video Thumbnail Unavailable'
                    iframe.parentNode?.replaceChild(fallback, iframe)
                  }
                } else {
                  iframe.remove()
                }
              } catch (error) {
                console.error('Error processing iframe:', error)
                iframe.remove()
              }
            })
          )

          // Apply styles to ALL text elements within the message
          messageClone
            .querySelectorAll(
              'div:not(.bg-card) p, div:not(.bg-card) h1, div:not(.bg-card) h2, div:not(.bg-card) h3, div:not(.bg-card) h4, div:not(.bg-card) h5, div:not(.bg-card) h6, div:not(.bg-card) a, div:not(.bg-card) ol, div:not(.bg-card) ul'
            )
            .forEach((element) => {
              const tagName = element.tagName.toLowerCase()
              let fontSize = '24px'
              let lineHeight = '1.4'

              switch (tagName) {
                case 'h1':
                  fontSize = '32px'
                  lineHeight = '1.2'
                  break
                case 'h2':
                  fontSize = '28px'
                  lineHeight = '1.3'
                  break
                case 'h3':
                  fontSize = '24px'
                  lineHeight = '1.4'
                  break
                case 'h4':
                  fontSize = '20px'
                  lineHeight = '1.5'
                  break
                case 'ol':
                case 'ul':
                  fontSize = '20px'
                  lineHeight = '1.5'
                  break
                case 'a':
                  fontSize = '20px'
                  lineHeight = '1.5'
                  break
                case 'h5':
                  fontSize = '18px'
                  lineHeight = '1.6'
                  break
                case 'h6':
                  fontSize = '16px'
                  lineHeight = '1.7'
                  break
                default:
                  fontSize = '24px'
                  lineHeight = '1.4'
              }

              ;(element as HTMLElement).style.cssText += `
        font-size: ${fontSize} !important;
        line-height: ${lineHeight} !important;
      `
            })

          // Remove other UI elements but preserve message content
          messageClone
            .querySelectorAll(
              '[class*="message-actions"], [class*="group-hover"], [class*="selection"], .fixed, [class*="action"], [class*="hover:opacity"], [class*="absolute"] , button:not(:has(img[alt="user image"])), .fixed'
            )
            .forEach((el) => el.remove())

          // Ensure message content is visible
          const messageContent = messageClone.querySelector('[class*="message-content"]')

          if (messageContent) {
            //  // Apply styles to only the primary text elements within message content
            //  messageContent.querySelectorAll('div:not(.bg-card)  p').forEach(element => {
            //   (element as HTMLElement).style.cssText += `
            //     font-size: 24px !important;

            //   `
            //          })

            messageContent.setAttribute(
              'style',
              `
            color: ${theme === 'dark' ? '#ffffff' : '#000000'} !important;
            font-size: 24px !important;
            line-height: 1.6 !important;
            white-space: pre-wrap !important;
            width: 100% !important;
            display: block !important;

          `
            )
          }

          // Ensure message container styles are preserved
          messageClone.style.cssText += `
          display: flex !important;
           font-size: 24px !important;
          align-items: start !important;
          padding: 6px !important;
          margin: 6px 0 !important;
          width: 100% !important;
          color: ${theme === 'dark' ? '#ffffff' : '#000000'} !important;
          gap: 16px !important;

        `

          messagesContainer.appendChild(messageClone)
        })
      )

      // Add additional delay to ensure all images are loaded
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Safely append to body with conflict prevention
      try {
        // Remove any existing screenshot containers first
        const existingContainers = document.querySelectorAll('[id^="screenshot-container-"]')
        existingContainers.forEach(el => el.remove())
        
        document.body.appendChild(container)

        const context = await getScreenshotContext(container)
        const dataUrl = await domToPng(context, {
          quality: 3.0,
          scale: 3
        })

        document.body.removeChild(container)

        // Convert dataUrl to blob
        const response = await fetch(dataUrl)
        const blob = await response.blob()

        // Download handling
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'chat-image.png'
        a.click()
        URL.revokeObjectURL(url)

        return blob
      } catch (error) {
        if (document.body.contains(container)) {
          document.body.removeChild(container)
        }
        throw error
      } finally {
        cleanupScreenshotContext()
      }
    } catch (error) {
      console.error('Error in generateShareImage:', error)
      toast.error("Couldn't generate that image. Try again.")
      return
    } finally {
      setIsProcessing(false)
    }
  }

  const fallbackShare = async (shareUrl: string, imageBlob: Blob) => {
    await navigator.clipboard.writeText(shareUrl)
    const imgUrl = URL.createObjectURL(imageBlob)
    window.open(imgUrl)
    toast.success('Share URL copied to clipboard')
  }

  const handleShare = async () => {
    if (!selectedChat || selectedMessages.length === 0) return

    try {
      const imageBlob = await generateShareImage()
      if (!imageBlob) return

      // const shareUrl = `${window.location.origin}/chat/share/${selectedMessages[selectedMessages.length - 1]}`

      // Try native share API first
      if (navigator.share && navigator.canShare) {
        const shareData: ShareData = {
          title: 'Shared Chat Conversation',
          text: 'Check out this chat conversation!'
        }
        // url: shareUrl

        // Only add file if browser supports it
        try {
          const file = new File([imageBlob], 'chat-share.png', {
            type: 'image/png'
          })
          if (navigator.canShare({ files: [file] })) {
            shareData.files = [file]
          }
        } catch {
          // Fall back to text-only native sharing.
        }

        try {
          await navigator.share(shareData)
          toast.success('Shared successfully!')
        } catch (err: unknown) {
          if (err instanceof Error && err.name !== 'AbortError') {
            // Fall back to clipboard if share fails (but not if user cancelled)
            // await fallbackShare(shareUrl, imageBlob)
            await fallbackShare('', imageBlob)
          }
        }
      } else {
        // Fallback for browsers that don't support the share API
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': imageBlob })])
        const imgUrl = URL.createObjectURL(imageBlob)
        window.open(imgUrl)
        toast.success('Share URL copied to clipboard')
      }

      setIsSelecting(false)
      setSelectedMessages([])
    } catch (error) {
      console.error('Error sharing:', error)
      toast.error("Couldn't share that image. Try again.")
    }
  }

  const handleShareButtonClick = () => {
    if (!isSelecting) {
      setIsSelecting(true)
      handleMessageSelect(messageId || '') // Pass the messageId to select
    }
  }

  useEffect(() => {
    return () => {
      cleanupScreenshotContext()
    }
  }, [])

  return (
    <>
      {!isSelecting && messageId && (
        <Button
          size="icon"
          variant="ghost"
          onClick={handleShareButtonClick}
          className="hover:bg-accent/50 size-5"
        >
          <IconShare2 size={MESSAGE_ICON_SIZE + 2} />
        </Button>
      )}

      {isSelecting && !messageId && (
        <div className="bg-background/80 inset-x-0 z-50 w-full border-b backdrop-blur-md">
          <div className="mx-auto flex h-14 items-center justify-between px-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">{selectedMessages.length} selected</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedMessages([])}
                className="text-xs"
              >
                Clear
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsSelecting(false)
                  setSelectedMessages([])
                }}
              >
                <IconX size={16} className="mr-1" />
                Cancel
              </Button>

              <Button
                size="sm"
                onClick={handleShare}
                disabled={selectedMessages.length === 0 || isProcessing}
              >
                {isProcessing ? (
                  <IconLoader2 className="mr-2 animate-spin" size={16} />
                ) : (
                  <IconShare2 size={16} className="mr-2" />
                )}
                {isProcessing ? 'Processing...' : 'Share'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
